import { useDarkMode } from '@/hooks/useDarkMode';
import { useState, useRef, useEffect, useMemo } from 'react';

import { Clock, Hash, Search, Sparkles, Tag, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
const RECENT_KEY = 'mementa.recent_searches';
const MAX_RECENT = 8;

function loadRecent() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecent(list) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
  } catch {
    // ignore
  }
}

export default function SearchBar({
  value,
  onChange,
  onClear,
  products = [],
  categories = [],
  tags = []
}) {
  const darkMode = useDarkMode();
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState(() => loadRecent());
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Compute live suggestions
  const suggestions = useMemo(() => {
    const q = (value || '').trim().toLowerCase();
    if (!q) return { products: [], categories: [], tags: [] };
    const productMatches = products
      .filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        (p.tags || []).some(t => (t || '').toLowerCase().includes(q))
      )
      .slice(0, 5);
    const catMatches = categories
      .filter(c => (c || '').toLowerCase().includes(q))
      .slice(0, 4);
    const tagMatches = tags
      .filter(t => (t || '').toLowerCase().includes(q) && (t || '').toLowerCase() !== q)
      .slice(0, 6);
    return { products: productMatches, categories: catMatches, tags: tagMatches };
  }, [value, products, categories, tags]);

  const commitSearch = (term) => {
    if (!term) return;
    const trimmed = term.trim();
    if (!trimmed) return;
    onChange(trimmed);
    // Persist recent
    setRecent(prev => {
      const next = [trimmed, ...prev.filter(r => r.toLowerCase() !== trimmed.toLowerCase())];
      saveRecent(next);
      return next.slice(0, MAX_RECENT);
    });
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitSearch(value);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const removeRecent = (term, e) => {
    e.stopPropagation();
    setRecent(prev => {
      const next = prev.filter(r => r !== term);
      saveRecent(next);
      return next;
    });
  };

  const clearAllRecent = () => {
    setRecent([]);
    saveRecent([]);
  };

  const showDropdown =
    open && (
      (value && (suggestions.products.length || suggestions.categories.length || suggestions.tags.length)) ||
      (!value && recent.length > 0)
    );

  const dropdownBg = darkMode.isDark ? '#1a1b23' : 'white';
  const dropdownBorder = darkMode.isDark ? '#3A3B45' : '#E5E5E5';
  const sectionLabelColor = darkMode.isDark ? '#FED7A5' : '#9E6752';
  const itemHoverBg = darkMode.isDark ? 'rgba(254, 215, 165, 0.08)' : 'rgba(158, 103, 82, 0.06)';

  return (
    <div ref={wrapRef} className="flex-1 w-full relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 z-10" style={{color: darkMode.textSecondary}} />
        <Input
          ref={inputRef}
          placeholder="Search by name, category, or tags..."
          data-testid="search-input"
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          className="pl-10 pr-10 py-2 w-full transition-colors"
          style={{
            borderRadius: '10px', 
            border: `2px solid ${darkMode.isDark ? '#3A3B45' : '#E5E5E5'}`,
            background: darkMode.isDark ? '#1a1b23' : 'white',
            color: darkMode.isDark ? '#FFFAF5' : '#20212B'
          }}
        />
        {value && (
          <button
            onClick={() => { onClear(); setOpen(true); inputRef.current?.focus(); }}
            data-testid="search-clear-btn"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 rounded-full p-1 transition-colors z-10"
            style={{
              background: darkMode.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
            }}
            aria-label="Clear search"
          >
            <X className="w-4 h-4" style={{color: darkMode.textSecondary}} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div
          data-testid="search-suggestions-dropdown"
          className="absolute left-0 right-0 mt-2 rounded-xl shadow-2xl overflow-hidden z-50"
          style={{
            background: dropdownBg,
            border: `1px solid ${dropdownBorder}`,
            maxHeight: '480px',
            overflowY: 'auto',
            overscrollBehavior: 'contain'
          }}
        >
          {/* Recent searches (when input empty) */}
          {!value && recent.length > 0 && (
            <div>
              <div className="flex items-center justify-between px-4 py-2 sticky top-0" style={{ background: dropdownBg }}>
                <p className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: sectionLabelColor }}>
                  <Clock className="w-3 h-3" />
                  Recent
                </p>
                <button
                  data-testid="clear-recent-searches-btn"
                  onClick={clearAllRecent}
                  className="text-[11px] font-medium hover:underline"
                  style={{ color: darkMode.textSecondary }}
                >
                  Clear all
                </button>
              </div>
              <ul>
                {recent.map((term, idx) => (
                  <li key={`recent-${idx}`}>
                    <div
                      data-testid={`recent-search-${idx}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => commitSearch(term)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          commitSearch(term);
                        }
                      }}
                      className="w-full flex items-center justify-between gap-2 px-4 py-2 text-left transition-colors group cursor-pointer outline-none"
                      onMouseEnter={(e) => e.currentTarget.style.background = itemHoverBg}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Clock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: darkMode.textMuted }} />
                        <span className="text-sm truncate" style={{ color: darkMode.textPrimary }}>{term}</span>
                      </div>
                      <button
                        onClick={(e) => removeRecent(term, e)}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded-full transition-opacity"
                        style={{ background: 'rgba(0,0,0,0.05)' }}
                        aria-label="Remove from recent"
                      >
                        <X className="w-3 h-3" style={{ color: darkMode.textSecondary }} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Live suggestions */}
          {value && suggestions.products.length > 0 && (
            <div>
              <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wider sticky top-0 flex items-center gap-1.5"
                 style={{ color: sectionLabelColor, background: dropdownBg }}>
                <Sparkles className="w-3 h-3" />
                Products
              </p>
              <ul>
                {suggestions.products.map((p) => (
                  <li key={`prod-${p.id}`}>
                    <button
                      data-testid={`search-suggest-product-${p.id}`}
                      onClick={() => commitSearch(p.name)}
                      className="w-full flex items-center gap-3 px-4 py-2 text-left transition-colors"
                      onMouseEnter={(e) => e.currentTarget.style.background = itemHoverBg}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      {p.image_url && (
                        <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0 bg-white">
                          <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate" style={{ color: darkMode.textPrimary }}>{p.name}</p>
                        <p className="text-[11px]" style={{ color: darkMode.textSecondary }}>${p.price}{p.category ? ` · ${p.category}` : ''}</p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {value && suggestions.categories.length > 0 && (
            <div>
              <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wider sticky top-0 flex items-center gap-1.5"
                 style={{ color: sectionLabelColor, background: dropdownBg }}>
                <Hash className="w-3 h-3" />
                Categories
              </p>
              <ul>
                {suggestions.categories.map((cat) => (
                  <li key={`cat-${cat}`}>
                    <button
                      data-testid={`search-suggest-category-${cat}`}
                      onClick={() => commitSearch(cat)}
                      className="w-full px-4 py-2 text-left text-sm transition-colors"
                      style={{ color: darkMode.textPrimary }}
                      onMouseEnter={(e) => e.currentTarget.style.background = itemHoverBg}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      {cat}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {value && suggestions.tags.length > 0 && (
            <div>
              <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wider sticky top-0 flex items-center gap-1.5"
                 style={{ color: sectionLabelColor, background: dropdownBg }}>
                <Tag className="w-3 h-3" />
                Tags
              </p>
              <div className="px-4 py-2 flex flex-wrap gap-1.5">
                {suggestions.tags.map((t) => (
                  <button
                    key={`tag-${t}`}
                    data-testid={`search-suggest-tag-${t}`}
                    onClick={() => commitSearch(t)}
                    className="px-2.5 py-1 rounded-full text-xs font-medium transition-all hover:scale-105"
                    style={{
                      background: darkMode.isDark ? 'rgba(254, 215, 165, 0.15)' : 'rgba(158, 103, 82, 0.1)',
                      color: darkMode.isDark ? '#FED7A5' : '#9E6752',
                      border: `1px solid ${darkMode.isDark ? 'rgba(254, 215, 165, 0.3)' : 'rgba(158, 103, 82, 0.2)'}`
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
